import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import Icon from '../Icon/Icon';
import { cn } from '../../utils';
import { createDateString } from './utils';
import { useEffect, useState } from 'react';
import { DateRangeString } from './components/DateRangeString';
import type { DatePickerProps } from './DatePicker.types';
import { DatePickerPopoverContent } from './components/DatePickerPopoverContent';
import { cva } from 'class-variance-authority';

const DatePicker = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  DatePickerProps
>(
  (
    {
      value,
      placeholder = 'Select a date range',
      onChange,
      calendarProps,
      calendarAlign = 'start',
      range,
      fixedDatesShortcut,
      fixedDatesList,
      className,
      size,
      onCaptureAction,
      errors,
      popoverModalMode = false,
      showApplyButton = false,
      ...props
    },
    ref
  ): JSX.Element => {
    const [openPopover, setOpenPopover] = React.useState(false);
    // Used to store the date value locally until "Apply" is clicked
    const [tempDateValue, setTempDateValue] = useState<Date | [Date, Date] | null>(
      value || null
    );
    // In a range, there are two calendars, start and end. - this defines which one is selected.
    const [selectedCalendarRange, setSelectedCalendarRange] = React.useState<
      'start' | 'end' | undefined
    >(undefined);

    const hasErrors = Boolean(errors?.length);

    useEffect(() => {
      setTempDateValue(value);
    }, [value, openPopover]);

    const onOpenChange = (isOpen: boolean) => {
      setOpenPopover(isOpen);
      // If the popover is closed and showApplyButton is false, then set the date value
      // whenever the popover is closed.
      if (!isOpen && !showApplyButton) {
        onChange && onChange(tempDateValue as any);
      }
      // If the popover is closed and showApplyButton is true, then we don't want to set the date value.
    };

    return (
      <PopoverPrimitive.Root
        open={openPopover}
        onOpenChange={onOpenChange}
        modal={popoverModalMode}
      >
        <fieldset className={cn('sui-w-fit', className)}>
          <PopoverPrimitive.Trigger
            className={cn(
              datePickerVariants({ size, error: hasErrors }),
              'sui-cursor-pointer'
            )}
            ref={ref}
            {...props}
          >
            {
              <DatePickerTriggerContent
                range={range}
                value={value}
                openPopover={openPopover}
                dateSelected={selectedCalendarRange}
                placeholder={placeholder}
              />
            }
          </PopoverPrimitive.Trigger>
          {hasErrors &&
            errors.map((error) => (
              <div className='sui-text-negative-text sui-caption sui-block sui-mt-0.5'>
                {error}
              </div>
            ))}
        </fieldset>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            className='sui-bg-white sui-rounded sui-border sui-border-solid sui-border-gray-90 sui-shadow-up sui-z-[99999]'
            sideOffset={8}
            align={calendarAlign}
            data-testid='snap-ui-calendar-popover-content'
          >
            <DatePickerPopoverContent
              range={range}
              dateValue={tempDateValue}
              setDateValue={setTempDateValue}
              setDateSelected={setSelectedCalendarRange}
              calendarProps={calendarProps}
              onCaptureAction={onCaptureAction}
              setOpenPopover={setOpenPopover}
              showFooter={showApplyButton}
              fixedDatesShortcut={fixedDatesShortcut}
              fixedDatesList={fixedDatesList}
              onChange={onChange}
            />
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  }
);

function DatePickerTriggerContent({
  range,
  value,
  openPopover,
  dateSelected,
  placeholder,
}: {
  range: boolean;
  value: Date | [Date, Date] | null;
  openPopover: boolean;
  dateSelected: 'start' | 'end' | undefined;
  placeholder: string;
}) {
  if (!value || (range && !value[0] && value[1])) {
    return (
      <>
        <Icon name='calendar_month' className='sui-text-neutral-icon-medium' />
        <span className='sui-text-neutral-text-weak sui-mr-auto'>{placeholder}</span>
      </>
    );
  }
  if (range) {
    return (
      <DateRangeString
        isPopoverOpen={openPopover}
        dateValues={value as [Date, Date]}
        dateJustSelected={dateSelected}
      />
    );
  }
  return (
    <span className='sui-flex sui-items-center sui-gap-1'>
      <Icon name='calendar_month' className='sui-text-neutral-icon-medium' />{' '}
      {createDateString(value as Date)}
    </span>
  );
}

export const datePickerVariants = cva(
  [
    'sui-w-full sui-border sui-border-solid sui-border-neutral-border sui-bg-white sui-rounded sui-text-desktop-5 sui-flex sui-items-center sui-gap-2 sui-px-1 sui-justify-between',
    'hover:sui-border-action-border-hover',
    'data-[state=open]:sui-border-action-background',
  ],
  {
    variants: {
      size: {
        small: 'sui-h-[32px]',
        default: 'sui-h-[48px]',
        large: 'sui-h-[56px]',
      },
      error: {
        true: 'sui-border-negative-border-hover',
      },
    },
    defaultVariants: { size: 'default' },
  }
);

export default DatePicker;
